import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { resolveUserLandingPath } from "@/lib/userAccess";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import airowLogo from "@/assets/airow-logo.png";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n/config";
import enCA from "@/i18n/locales/en-CA.json";
import frCA from "@/i18n/locales/fr-CA.json";

const Auth = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    // Check if user is already logged in
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        redirectBasedOnRole(session.user.id);
      }
    });
  }, [navigate]);

  const redirectBasedOnRole = async (userId: string) => {
    try {
      navigate(await resolveUserLandingPath(userId));
    } catch (error) {
      console.error("Error determining redirect:", error);
      navigate("/dashboard");
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Get the user's session to load their language preference
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session) {
        // Fetch user's language preference
        const { data: profile } = await supabase
          .from("profiles")
          .select("language")
          .eq("id", session.user.id)
          .maybeSingle();

        // Update i18n language if user has a preference
        if (profile?.language) {
          await i18n.changeLanguage(profile.language);
        }

        // Show welcome message in user's language
        toast({
          title: t("auth.welcomeBack"),
          description: t("auth.signedInSuccess"),
        });

        await redirectBasedOnRole(session.user.id);
      }
    } catch (error: any) {
      toast({
        title: t("auth.errorSigningIn"),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-dark p-4 relative overflow-hidden">
      {/* Background Video */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
        onError={(e) => { (e.target as HTMLVideoElement).style.display = 'none'; }}
      >
        <source
          src="https://marquiscote.com/airow/airow-background-video_sm.mp4"
          type="video/mp4"
          onError={(e) => {
            const video = (e.target as HTMLSourceElement).parentElement as HTMLVideoElement;
            if (video) video.style.display = 'none';
          }}
        />
      </video>

      {/* Overlay for better readability */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Content */}
      <Card className="w-full max-w-md shadow-glow border-border/50 relative z-10">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <img src={airowLogo} alt="Airow Logo" className="h-12" />
          </div>
          <CardTitle className="text-2xl font-display text-center">
            {enCA.auth.signIn} / {frCA.auth.signIn}
          </CardTitle>
          <CardDescription className="text-center text-muted-foreground">
            <span className="block">{enCA.auth.signInDescription}</span>
            <span className="block">{frCA.auth.signInDescription}</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSignIn} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">
                {enCA.auth.email} / {frCA.auth.email}
              </Label>
              <Input
                id="email"
                type="email"
                placeholder={enCA.auth.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">
                {enCA.auth.password} / {frCA.auth.password}
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {enCA.auth.signIn} / {frCA.auth.signIn}
            </Button>
          </form>
        </CardContent>
      </Card>
      <footer className="mt-8 text-center text-sm text-muted-foreground relative z-10">
        <p className="mb-2">© {new Date().getFullYear()} Airow. All rights reserved. / Tous droits réservés.</p>
        <p>
          Visit / Visitez{" "}
          <a href="https://airow.ca" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
            Airow.ca
          </a>{" "}
          for more information about our services. / pour plus d'informations sur nos services.
        </p>
      </footer>
    </div>
  );
};

export default Auth;
